import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  X, Loader2, AlertTriangle, Info, Receipt, HandCoins, CheckCircle2, Lock, FlaskConical,
} from 'lucide-react';
import { supabase } from '../../../../src/lib/supabase';
import { buscarPagadorDaUc } from '../../../../src/services/pagadores';
import { useAuth } from '../../../../src/contexts/AuthContext';
import { useToast } from '../../../../src/contexts/ToastContext';
import { logAction, logUsageEvent } from '../../../../src/lib/logger';
import { propsFundoModal } from '../../../../src/lib/backdropModal';
import { addDaysISO } from '../../../../src/lib/janelaCobranca';
import { MESES_ABREV } from '../../../../src/lib/mesReferencia';
import { emitirCobrancaAvulsa } from '../../../../src/lib/emitirCobrancaAvulsa';
import { valorDevido } from '../../../../src/lib/statusBoleto';
import {
  CANAIS_CONTATO, MOTIVOS_NEGOCIACAO,
  descricaoCobranca, emCentavos, snapshotDasFaturas, totalDasFaturas, validarCobrancaUnificada,
  type FaturaParaUnificar,
} from '../../../../src/lib/cobrancaUnificada';
import { formatCurrency } from '../utils';

// Cobrança unificada — a negociação que junta as faturas em aberto do cliente
// num boleto só.
//
// Substitui o par manual que existia antes: emitir um avulso pela tela de
// Faturas e, dias depois, lembrar de abrir "Quitar Faturas" e marcar na mão o
// que aquele boleto cobriu. Aqui o vínculo nasce junto com o boleto, e quem
// quita é o banco quando o pagamento entra (trigger
// `trg_quitar_por_cobranca_unificada`, migration 20260917140000).
//
// A tela é um formulário de negociação, não de emissão: o que ela exige a mais
// que o boleto avulso é o registro de POR QUE a cobrança existe — motivo, canal
// e observação. É esse registro que responde "o cliente pagou porque alguém
// correu atrás?", que é como o financeiro mede a própria eficiência.
//
// Emitir aqui NÃO muda nenhuma fatura. Se o cliente não pagar, a dívida
// continua inteira e cobrável, com os boletos originais de pé — mesma regra do
// acordo de parcelamento.
//
// Visual: segue a linguagem do resto da Central de Inadimplência (superfície
// clara, verde da marca em hex como os arquivos vizinhos, cartões de borda
// fina). Consistência dentro do módulo vale mais aqui do que estilo próprio —
// quem usa esta tela passa o dia nas outras.

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  clienteNome: string;
  clienteCpfCnpj?: string | null;
}

const STATUS_FATURA_COM_BOLETO = [
  'Com boleto calculado',
  'Com boleto emitido / A enviar',
  'Cobrança enviada',
];

const CAMPO = 'w-full px-3 py-2.5 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#5F6C37]/20 focus:border-[#5F6C37] outline-none transition-all text-sm';
const ROTULO = 'text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-1 block';

const formatDateBR = (s: string | null | undefined) => {
  if (!s) return '—';
  const [a, m, d] = String(s).split('T')[0].split('-');
  return `${d}/${m}/${a}`;
};

/** Mês de referência canônico de hoje ('SET/2026') — o boleto é do mês em que a
 *  negociação aconteceu, não dos meses cobrados. */
const mesReferenciaDeHoje = (): string => {
  const d = new Date();
  return `${MESES_ABREV[d.getMonth()]}/${d.getFullYear()}`;
};

const hojeIso = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const CobrancaUnificadaModal: React.FC<Props> = ({ isOpen, onClose, onSuccess, clienteNome, clienteCpfCnpj }) => {
  const { user, profile } = useAuth();
  const { addToast } = useToast();

  const [carregando, setCarregando] = useState(false);
  const [emitindo, setEmitindo] = useState(false);
  const [faturas, setFaturas] = useState<FaturaParaUnificar[]>([]);
  /** Faturas presas em outra cobrança unificada ainda aguardando pagamento. */
  const [bloqueadas, setBloqueadas] = useState<Record<string, boolean>>({});
  const [selecionadas, setSelecionadas] = useState<Set<string>>(new Set());

  const [vencimento, setVencimento] = useState('');
  /** Vazio = acompanha a soma das faturas. Preenchido = desconto/acréscimo digitado. */
  const [valorManual, setValorManual] = useState('');
  const [motivo, setMotivo] = useState<string>('');
  const [canal, setCanal] = useState<string>('');
  const [observacao, setObservacao] = useState('');
  const [erroEmissao, setErroEmissao] = useState<string | null>(null);
  // Emissão de teste: nenhum boleto real, fatura marcada como sandbox e fora de
  // todo relatório. Só adm — a edge `emitir-boleto-teste` também barra os outros.
  const [emitirEmSandbox, setEmitirEmSandbox] = useState(false);
  const ehAdm = profile?.role === 'adm';

  // Abrir o modal zera o formulário. Separado da carga de propósito: enquanto os
  // dois estavam juntos, marcar "emitir em sandbox" disparava este bloco (que
  // depende do ambiente para recarregar as faturas) e o próprio reset
  // desmarcava a caixa no mesmo instante — impossível ligar o modo teste.
  useEffect(() => {
    if (!isOpen) return;
    setSelecionadas(new Set());
    setValorManual('');
    setMotivo('');
    setCanal('');
    setObservacao('');
    setErroEmissao(null);
    setEmitirEmSandbox(false);
    // 5 dias corridos, pulando o fim de semana: prazo que a operação já usa para
    // boleto negociado no telefone.
    setVencimento(addDaysISO(hojeIso(), 5));
  }, [isOpen]);

  // Carga das faturas: refaz quando troca o cliente ou o ambiente (teste x real).
  useEffect(() => {
    if (!isOpen) return;
    setFaturas([]);
    setBloqueadas({});
    setSelecionadas(new Set());

    let cancelado = false;
    (async () => {
      setCarregando(true);
      try {
        const doc = (clienteCpfCnpj || '').replace(/\D/g, '');
        let q = supabase
          .from('faturas')
          .select('id, uc_codigo, cliente_nome, cliente_cpf_cnpj, mes_referencia, valor_boleto_vigor, valor_emissao_boleto_unico, vencimento_boleto, data_vencimento, status_boleto_vigor, status_fatura, num_cobranca_boleto, data_pgto_boleto, quitado_em, energia_injetada_gd1_kwh, energia_injetada_gd2_kwh')
          .in('status_boleto_vigor', ['Em aberto', 'Vencido'])
          .in('status_fatura', STATUS_FATURA_COM_BOLETO)
          .is('parcelamento_origem_id', null)
          .order('vencimento_boleto', { ascending: true });

        // Fatura de teste nunca entra numa cobrança real e vice-versa: misturar
        // as duas poria uma cobrança fictícia no boleto do cliente.
        q = emitirEmSandbox ? q.eq('ambiente', 'sandbox') : q.or('ambiente.is.null,ambiente.neq.sandbox');

        // O CPF/CNPJ é o que identifica o titular, mas 331 das faturas vencidas
        // estão sem ele (medido em 12/08/2026) — por isso o nome entra junto.
        q = doc ? q.or(`cliente_cpf_cnpj.eq.${doc},cliente_nome.eq.${clienteNome}`) : q.eq('cliente_nome', clienteNome);

        const { data, error } = await q;
        if (error) throw error;
        if (cancelado) return;

        const lista = (data || []) as FaturaParaUnificar[];
        setFaturas(lista);

        // Fatura já comprometida com outra negociação viva não pode entrar de
        // novo: sairiam dois boletos cobrando a mesma dívida. O banco também
        // barra (trg_cobranca_unificada_sem_conflito), mas o operador precisa
        // ver isso ANTES de montar a cobrança.
        if (lista.length > 0) {
          const { data: presas } = await supabase
            .from('cobranca_unificada_faturas')
            .select('fatura_id, cobrancas_unificadas!inner(status)')
            .in('fatura_id', lista.map((f) => f.id))
            .eq('cobrancas_unificadas.status', 'aguardando_pagamento');
          if (!cancelado) {
            const mapa: Record<string, boolean> = {};
            (presas || []).forEach((p: any) => { mapa[String(p.fatura_id)] = true; });
            setBloqueadas(mapa);
          }
        }
      } catch (e: any) {
        if (!cancelado) addToast(`Erro ao carregar as faturas do cliente: ${e.message || e}`, 'error');
      } finally {
        if (!cancelado) setCarregando(false);
      }
    })();

    return () => { cancelado = true; };
  }, [isOpen, clienteNome, clienteCpfCnpj, emitirEmSandbox]);

  const faturasSelecionadas = useMemo(
    () => faturas.filter((f) => selecionadas.has(String(f.id))),
    [faturas, selecionadas],
  );

  const soma = useMemo(() => emCentavos(totalDasFaturas(faturasSelecionadas)), [faturasSelecionadas]);

  // O valor do boleto é derivado da soma; digitar substitui. Mudar a seleção
  // devolve o controle para a soma de propósito: sem isso, marcar mais uma
  // fatura deixaria o boleto com o valor antigo — cobrança a menos, silenciosa.
  const valorCobrado = valorManual === '' ? soma : Number(valorManual) || 0;
  useEffect(() => { setValorManual(''); }, [selecionadas]);

  const validacao = useMemo(() => validarCobrancaUnificada({
    faturas: faturasSelecionadas,
    valorCobrado,
    motivo,
    canal,
    observacao,
  }), [faturasSelecionadas, valorCobrado, motivo, canal, observacao]);

  const alternar = (f: FaturaParaUnificar) => {
    const id = String(f.id);
    if (bloqueadas[id]) return;
    setSelecionadas((prev) => {
      const prox = new Set(prev);
      if (prox.has(id)) prox.delete(id); else prox.add(id);
      return prox;
    });
  };

  const emitir = async () => {
    if (!validacao.ok || !vencimento) return;
    setEmitindo(true);
    setErroEmissao(null);

    const total = valorCobrado;
    const ucBase = String(faturasSelecionadas[0]?.uc_codigo || '');
    const nomeQuem = profile?.full_name || user?.email || 'Sistema';
    const descricao = descricaoCobranca(faturasSelecionadas);

    try {
      // Dados do pagador: o cadastro da UC manda, porque é dele que sai o boleto.
      // A fatura só serve de reserva quando a UC não está cadastrada.
      const { data: ucList } = await supabase.rpc('resolver_uc_por_codigo', { p_codigo: ucBase });
      const uc = (Array.isArray(ucList) ? ucList[0] : ucList) || null;
      const base = faturasSelecionadas[0] as any;
      // Pagador da UC (tabela pagadores) vem antes do cadastro: nome e documento juntos.
      const pagadorUc = uc?.id ? await buscarPagadorDaUc(uc.id) : null;

      let cobrancaId: string | null = null;

      const { faturaId } = await emitirCobrancaAvulsa({
        ucCodigo: ucBase,
        nomeQuem,
        sandbox: emitirEmSandbox,
        campos: {
          uc_codigo: ucBase,
          cliente_nome: uc?.nome_cliente || base?.cliente_nome || clienteNome,
          cliente_cpf_cnpj: uc?.cpf_cnpj_cliente || uc?.cpf_cnpj_titular || base?.cliente_cpf_cnpj || clienteCpfCnpj || '',
          nome_cliente_boleto: pagadorUc ? pagadorUc.nome : (uc?.nome_cliente || base?.cliente_nome || clienteNome),
          cpf_cnpj_cliente_boleto: pagadorUc ? (pagadorUc.cpf_cnpj || '') : (uc?.cpf_cnpj_cliente || uc?.cpf_cnpj_titular || base?.cliente_cpf_cnpj || clienteCpfCnpj || ''),
          end_logradouro: uc?.logradouro || '',
          end_numero: uc?.numero || '',
          end_bairro: uc?.bairro || '',
          end_cep: uc?.cep || '',
          end_cidade: uc?.cidade || '',
          end_uf: uc?.estado || '',
          mes_referencia: mesReferenciaDeHoje(),
          valor_boleto_vigor: total,
          vencimento_boleto: vencimento,
          data_vencimento: vencimento,
          // O total vai na linha de "parcelamentos/outros" com a descrição dos
          // meses: é o que o cliente lê no boleto para saber o que está pagando.
          valor_parcelamentos_boleto: total,
          parcelamento_descricao: descricao,
          total_com_vigor: 0,
          economia: 0,
          custo_s_vigor: 0,
          observacoes_boleto: `${descricao}. Ao pagar este boleto, as faturas acima ficam quitadas.`,
        },
        // Vínculo gravado antes de o boleto existir — ver o comentário do helper.
        aposCriarFatura: async (idAvulsa) => {
          const { data: cobranca, error: erroCobranca } = await supabase
            .from('cobrancas_unificadas')
            .insert({
              cliente_nome: clienteNome,
              cliente_cpf_cnpj: (clienteCpfCnpj || base?.cliente_cpf_cnpj || '').replace(/\D/g, '') || null,
              fatura_avulsa_id: idAvulsa,
              valor_faturas: soma,
              valor_cobrado: total,
              motivo,
              canal,
              observacao: observacao.trim(),
              criado_por: user?.id || null,
              criado_por_nome: nomeQuem,
              faturas_snapshot: snapshotDasFaturas(faturasSelecionadas),
            })
            .select('id')
            .single();
          if (erroCobranca) throw new Error('Não foi possível registrar a negociação: ' + erroCobranca.message);
          cobrancaId = cobranca.id;

          const { error: erroItens } = await supabase
            .from('cobranca_unificada_faturas')
            .insert(faturasSelecionadas.map((f) => ({
              cobranca_unificada_id: cobranca.id,
              fatura_id: f.id,
              valor: emCentavos(valorDevido(f)),
            })));
          if (erroItens) throw new Error('Não foi possível vincular as faturas: ' + erroItens.message);
        },
      });

      await logAction(user?.id || 'SYSTEM', 'EMITIR_COBRANCA_UNIFICADA', {
        cobranca_unificada_id: cobrancaId,
        fatura_avulsa_id: faturaId,
        faturas: faturasSelecionadas.map((f) => f.id),
        valor_faturas: soma,
        valor_cobrado: total,
        motivo,
        canal,
        sandbox: emitirEmSandbox,
      });
      logUsageEvent({
        userId: user?.id, modulo: 'financeiro', funcionalidade: 'inadimplencia',
        acao: 'emitir_cobranca_unificada', origem: 'acao',
      });

      addToast(
        `Boleto único gerado (${faturasSelecionadas.length} faturas, ${formatCurrency(total)}). `
        + 'Quando o pagamento entrar, elas viram Quitado sozinhas.',
        'success',
      );
      onSuccess?.();
      onClose();
    } catch (e: any) {
      setErroEmissao(e?.message || 'Erro inesperado ao gerar a cobrança.');
    } finally {
      setEmitindo(false);
    }
  };

  if (!isOpen) return null;

  const diferenca = emCentavos(valorCobrado - soma);

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/70 backdrop-blur-sm p-0 sm:p-4"
      style={{ zIndex: 9999 }}
      {...propsFundoModal(() => onClose())}
    >
      <div
        className="bg-gray-50 w-full max-w-3xl sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[95vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-5 bg-white border-b border-gray-200 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-[#5F6C37]/10 rounded-xl text-[#5F6C37]"><HandCoins size={22} /></div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Cobrança unificada</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Um boleto só para várias faturas de {clienteNome}. Pagou, elas viram Quitado sozinhas.
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Faturas */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <span className="text-xs font-bold text-gray-500 uppercase tracking-wide flex items-center gap-2">
                <Receipt size={14} /> Faturas em aberto e vencidas
              </span>
              <span className="text-xs text-gray-400">{faturasSelecionadas.length} de {faturas.length} marcadas</span>
            </div>

            {carregando ? (
              <div className="p-8 flex items-center justify-center text-gray-400 gap-2">
                <Loader2 className="animate-spin" size={18} /> Carregando as faturas do cliente...
              </div>
            ) : faturas.length === 0 ? (
              <div className="p-8 text-center text-sm text-gray-500">
                Nenhuma fatura em aberto ou vencida para este cliente.
              </div>
            ) : (
              <div className="divide-y divide-gray-100 max-h-72 overflow-y-auto">
                {faturas.map((f) => {
                  const id = String(f.id);
                  const presa = !!bloqueadas[id];
                  const marcada = selecionadas.has(id);
                  return (
                    <label
                      key={id}
                      className={`flex items-center gap-3 px-4 py-3 ${presa ? 'bg-gray-50 cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-gray-50'}`}
                    >
                      <input
                        type="checkbox"
                        checked={marcada}
                        disabled={presa}
                        onChange={() => alternar(f)}
                        className="w-4 h-4 rounded border-gray-300 text-[#5F6C37] focus:ring-[#5F6C37]"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-gray-800">
                          {f.mes_referencia} <span className="font-normal text-gray-400">· UC {f.uc_codigo}</span>
                        </p>
                        <p className="text-[11px] text-gray-500">
                          Vence {formatDateBR(f.vencimento_boleto || f.data_vencimento)}
                          {presa && (
                            <span className="ml-2 inline-flex items-center gap-1 text-amber-600 font-semibold">
                              <Lock size={11} /> já está em outra cobrança unificada
                            </span>
                          )}
                        </p>
                      </div>
                      <span className="text-sm font-black text-gray-800">{formatCurrency(valorDevido(f))}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {/* Valor e vencimento */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <span className={ROTULO}>Soma das faturas</span>
              <p className="text-xl font-black text-gray-800">{formatCurrency(soma)}</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <label className={ROTULO}>Valor do boleto</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={valorManual === '' ? (soma > 0 ? soma.toFixed(2) : '') : valorManual}
                onChange={(e) => setValorManual(e.target.value)}
                className={CAMPO}
              />
              {Math.abs(diferenca) > 0.05 && (
                <p className={`text-[11px] mt-1 font-semibold ${diferenca < 0 ? 'text-amber-600' : 'text-blue-600'}`}>
                  {diferenca < 0 ? 'Desconto' : 'Acréscimo'} de {formatCurrency(Math.abs(diferenca))}
                </p>
              )}
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <label className={ROTULO}>Vencimento</label>
              <input
                type="date"
                value={vencimento}
                onChange={(e) => setVencimento(e.target.value)}
                className={CAMPO}
              />
            </div>
          </div>

          {/* Registro da negociação */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Registro da negociação</p>
              <p className="text-[11px] text-gray-500 mt-1">
                Fica no relatório do financeiro: é o que mostra que a fatura só foi quitada porque
                alguém entrou em contato.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={ROTULO}>Motivo</label>
                <select value={motivo} onChange={(e) => setMotivo(e.target.value)} className={CAMPO}>
                  <option value="">Escolha...</option>
                  {MOTIVOS_NEGOCIACAO.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className={ROTULO}>Canal do contato</label>
                <select value={canal} onChange={(e) => setCanal(e.target.value)} className={CAMPO}>
                  <option value="">Escolha...</option>
                  {CANAIS_CONTATO.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className={ROTULO}>Observação</label>
              <textarea
                value={observacao}
                onChange={(e) => setObservacao(e.target.value)}
                rows={3}
                placeholder="O que foi combinado com o cliente, com quem você falou, o que ele prometeu."
                className={CAMPO}
              />
            </div>
          </div>

          {/* Erros e avisos */}
          {validacao.erros.length > 0 && faturasSelecionadas.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-1">
              {validacao.erros.map((e) => (
                <p key={e} className="text-xs text-red-700 flex items-start gap-2">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" /> {e}
                </p>
              ))}
            </div>
          )}
          {validacao.avisos.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-1">
              {validacao.avisos.map((a) => (
                <p key={a} className="text-xs text-amber-800 flex items-start gap-2">
                  <Info size={14} className="mt-0.5 shrink-0" /> {a}
                </p>
              ))}
            </div>
          )}
          {erroEmissao && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <p className="text-xs text-red-700 whitespace-pre-line">{erroEmissao}</p>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-2">
            <Info size={14} className="mt-0.5 shrink-0 text-blue-600" />
            <p className="text-xs text-blue-800">
              Gerar o boleto não muda nada nas faturas: elas continuam vencidas e cobráveis, com os
              boletos atuais valendo. A quitação acontece quando o pagamento deste boleto entrar — aí
              as faturas viram <strong>Quitado</strong> e os boletos antigos são baixados no banco.
            </p>
          </div>
        </div>

        <div className="px-6 py-4 bg-white border-t border-gray-200 flex items-center justify-between gap-3 flex-wrap">
          {ehAdm ? (
            <label
              className="flex items-center gap-2 cursor-pointer select-none order-1"
              title="Não emite boleto nenhum: a fatura é preenchida com dados fictícios, fica marcada como teste e some de todos os relatórios."
            >
              <input
                type="checkbox"
                checked={emitirEmSandbox}
                onChange={(e) => setEmitirEmSandbox(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-amber-500 focus:ring-amber-400"
              />
              <span className={`text-xs font-semibold flex items-center gap-1.5 ${emitirEmSandbox ? 'text-amber-600' : 'text-gray-400'}`}>
                <FlaskConical size={14} /> Emitir em sandbox (teste)
              </span>
            </label>
          ) : null}
          <p className="text-xs text-gray-500 order-3 sm:order-2">
            {faturasSelecionadas.length > 0
              ? <>Boleto de <strong className="text-gray-800">{formatCurrency(valorCobrado)}</strong> cobrindo {faturasSelecionadas.length} faturas</>
              : 'Marque as faturas que entram na cobrança.'}
          </p>
          <div className="flex gap-2 order-2 sm:order-3 ml-auto">
            <button onClick={onClose} className="px-4 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-100 rounded-xl transition-colors">
              Cancelar
            </button>
            <button
              onClick={emitir}
              disabled={!validacao.ok || emitindo || !vencimento}
              className={`px-5 py-2.5 text-white text-sm font-bold rounded-xl disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-2 ${emitirEmSandbox ? 'bg-amber-500 hover:bg-amber-600' : 'bg-[#5F6C37] hover:bg-[#4d5830]'}`}
            >
              {emitindo
                ? <><Loader2 size={16} className="animate-spin" /> Gerando boleto...</>
                : emitirEmSandbox
                  ? <><FlaskConical size={16} /> Gerar boleto de teste</>
                  : <><CheckCircle2 size={16} /> Gerar boleto único</>}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default CobrancaUnificadaModal;
